import type { WireBuyer, WireIndexEntry, WireIntro, WireProfileChange, WireProspect, WireProspectRecord, WireSample, WireShortlistFull, WireShortlistRow, WireStrategy } from '@/api/wire';
import type { ItemContext, Party } from '@/ui/context';
import type { Who } from '@/ui/who';
import { countryName, dayWords, prospectSourceWords, stateWords } from '@/ui/words';
import { toolName } from '@/app/technical';
import { EXCLUSION_WORDS, STAGE_WORDS } from '@/outreach/words';
import { LOST_WORDS, productName, shortDate } from './words';
import { byName, clip, daysSince, pressedBy, say, signedInBy, sinceWords, soleAgent, withRole, type Reader } from './explain';

/**
 * What the follow-through records say about themselves (ADR-0155, ADR-0136):
 * shortlists and strategies an assistant proposed, changes proposed to a
 * profile, introductions, samples, a row of our index, a buyer, and a
 * prospect. Same rules as `explain.ts`: built from the row in hand, a field
 * the platform does not hold is left out or said to be unrecorded, the
 * platform sends nothing, and an assistant's proposal waits for a person.
 */

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

// --- Shortlists and strategies ---------------------------------------------------------

/** Whether it came from a research answer a lead took (ADR-0175), not from an assistant. */
export const fromResearch = (viaClient: string | null): boolean => viaClient === 'research' || (viaClient?.startsWith('research:') ?? false);

/** Who proposed it, as a party: the assistant a person used. The person is not on the row. */
const assistant = (viaClient: string | null, when: string | null): Party & { when?: string | undefined } => ({
  name: viaClient ?? 'An assistant',
  kind: 'agent',
  role: 'an assistant a person signed in',
  ...(when ? { when } : {}),
});

/**
 * A shortlist: businesses an assistant a person signed in named for a market.
 * Nothing is looked up or filed until a lead or a superadmin confirms it
 * under a running plan of that market; it confirms itself only after five
 * clean confirmations in a row (Amendment 0012, ADR-0153).
 */
export function explainShortlist(
  s: WireShortlistRow | WireShortlistFull,
  who: Who,
  reader: Reader,
  extra: { runningPlans?: number } = {},
): ItemContext {
  const country = countryName(s.marketCode);
  const count = 'count' in s ? s.count : s.candidates.length;
  const resolved = 'resolved' in s ? s.resolved : s.candidates.filter((c) => c.outcome === 'new' || c.outcome === 'known').length;
  const lead = who.lead(reader.department);
  const waitingOn = lead && lead.name !== 'You' ? lead.name : 'the department’s lead';
  const created = shortDate(s.createdAt);
  const research = fromResearch(s.viaClient);
  const base = {
    subject: { kind: 'shortlist', title: s.title },
    summary: research
      ? `${plural(count, 'business', 'businesses')} a research answer named in ${country}, taken by a lead to be found under a running plan: “${s.title}”.`
      : `${s.viaClient ?? 'An assistant'} a person signed in named ${plural(count, 'business', 'businesses')} in ${country}: “${s.title}”.${s.changed ? ' A person changed the list before deciding it.' : ''}`,
    source: research
      ? { label: 'Taken from a research answer', ...(created ? { when: created } : {}) }
      : { label: `${s.viaClient ? `Proposed by ${s.viaClient}` : 'Proposed by an assistant'} a person signed in`, ...(created ? { when: created } : {}) },
    broughtBy: research ? null : assistant(s.viaClient, created),
    owner: lead,
    involved: s.state === 'proposed' || s.state === 'declined' ? undefined : withRole(who.agentsOf(reader.department), 'looks the businesses up'),
    knows: who.knowers(reader.department),
    facts: [
      ['Businesses', String(count)],
      ['Signed in by', signedInBy(s, reader)],
      ['Looked up', s.state === 'proposed' || s.state === 'declined' ? null : `${resolved} of ${count}`],
      ['Decided', s.decidedAt ? dayWords(s.decidedAt) : null],
    ] as ItemContext['facts'],
    open: { to: `/prospecting/shortlists/${s.id}` },
  };
  if (s.state === 'proposed') {
    const noPlan = extra.runningPlans === 0;
    return {
      ...base,
      need: reader.decides ? `Your confirmation under a running plan in ${country}, or your decline.` : 'The department’s lead to confirm or decline it.',
      why: 'An assistant’s proposal does nothing until a person confirms it. It is waiting for the lead or a superadmin; this department has not yet earned confirming on its own record.',
      next: reader.decides
        ? noPlan
          ? {
              tip: `No plan is running in ${country}, so it cannot be confirmed yet. Set a plan up there first (a superadmin approves it), or decline the shortlist.`,
              instead: 'Declining looks nothing up and files nothing.',
              actions: [{ label: 'Set up a plan', to: `/prospecting/setup?market=${s.marketCode}`, primary: true }],
            }
          : {
              tip: `Read the ${plural(count, 'business', 'businesses')}, untick any you do not want, then confirm under a running plan in ${country}. Each is looked up as that plan’s agent and filed as a prospect. Leaving any out counts as a change, so the record towards confirming on its own starts again.`,
              instead: 'Or decline it: nothing is looked up or filed.',
            }
        : { tip: `Nothing is needed from you. ${waitingOn} confirms or declines it, and you can read the businesses on its page.`, instead: 'Tell them if one of the names is wrong.' },
      task: reader.decides ? { title: `Decide the shortlist “${clip(s.title, 60)}”`, from: `/prospecting/shortlists/${s.id}` } : undefined,
    };
  }
  if (s.state === 'confirmed' || s.state === 'resolving') {
    return {
      ...base,
      need: 'Nothing from you.',
      why: s.autoConfirmed ? 'It was confirmed on this department’s record of clean confirmations, without anyone pressing the button.' : 'A person confirmed it, so each business is being looked up as the plan’s agent.',
      next: { tip: 'Nothing is needed from you. The businesses are being looked up; what Google lists is filed as a prospect.', instead: 'Its page shows what each came to when it is done.' },
    };
  }
  if (s.state === 'resolved') {
    return {
      ...base,
      need: 'Nothing, unless a business should be written to.',
      why: s.autoConfirmed ? 'It was confirmed on this department’s record, and looked up as the plan’s agent.' : 'A person confirmed it, and each business was looked up as the plan’s agent.',
      next: {
        tip: `${resolved} of ${count} were looked up. Open its page to see which are new prospects, which were already ours and which Google does not list, then read the new prospects under Prospects.`,
        instead: 'A prospect is written to only after its company is approved and a person acts on its draft.',
        actions: [{ label: 'Open Prospects', to: '/prospects' }],
      },
    };
  }
  return {
    ...base,
    need: 'Nothing from you.',
    why: 'It was declined, so nothing was looked up or filed.',
    next: { tip: 'Nothing to do.', instead: 'If the businesses are still wanted, ask the assistant again for a fresh list.' },
  };
}

/** A strategy an assistant proposed: who to look for in a market, to set a plan up from. */
export function explainStrategy(s: WireStrategy, who: Who, reader: Reader): ItemContext {
  const country = countryName(s.marketCode);
  const created = shortDate(s.createdAt);
  const lead = who.lead(reader.department);
  const looks = s.body.looksFor && s.body.looksFor !== 'customers' ? (s.body.looksFor === 'suppliers' ? 'suppliers, never written to' : 'competitors, never written to') : 'customers';
  return {
    subject: { kind: 'strategy', title: s.title },
    summary: `${s.viaClient ?? 'An assistant'} a person signed in suggests looking for ${s.body.segment} (${looks}) in ${country}${s.body.cities?.length ? `, in ${s.body.cities.join(', ')}` : ''}${s.body.perDay ? `, ${s.body.perDay} a day` : ''}.`,
    need: s.state === 'proposed' ? (reader.decides ? 'Your choice: set a plan up from it, keep it as a template, or decline it.' : 'The department’s lead to decide it.') : undefined,
    why: 'A strategy is only an idea an assistant proposed: it sets nothing up. A plan is made by a person in Set up prospecting and waits for a superadmin.',
    source: { label: `${s.viaClient ? `Proposed by ${s.viaClient}` : 'Proposed by an assistant'} a person signed in`, ...(created ? { when: created } : {}) },
    broughtBy: assistant(s.viaClient, created),
    owner: lead,
    knows: who.knowers(reader.department),
    facts: [
      ['Rules proposed', s.body.rules?.length ? plural(s.body.rules.length, 'rule') : null],
      ['Signed in by', signedInBy(s, reader)],
      ['Its note', say(s.body.notes ?? null)],
    ],
    next:
      s.state === 'proposed'
        ? reader.decides
          ? {
              tip: 'Read what it suggests. If it is a good start, set up from it: the set-up opens with its answers filled in, and the plan you make waits for a superadmin.',
              instead: 'Or keep it as a template to set up from later, or decline it.',
              actions: [{ label: 'Set up from this', to: `/prospecting/setup?market=${s.marketCode}&strategy=${s.id}`, primary: true }],
            }
          : { tip: 'Nothing is needed from you. The department’s lead decides whether to set up from it.', instead: 'You can read it, and tell the lead what you think.' }
        : s.state === 'template'
          ? reader.decides
            ? { tip: 'It is kept as a template. Set up from it whenever a plan is wanted for this market.', instead: 'Nothing is lost by leaving it.', actions: [{ label: 'Set up from this', to: `/prospecting/setup?market=${s.marketCode}&strategy=${s.id}`, primary: true }] }
            : { tip: 'Nothing is needed from you. It is kept as a template.' }
          : s.state === 'used'
            ? { tip: 'Nothing to do: a plan was set up from it.' }
            : { tip: 'Nothing to do: it was declined.' },
  };
}

// --- Profile changes -------------------------------------------------------------------------

/**
 * A change proposed to a live profile, with what was counted (PR-19). A live
 * profile is never edited in place: a person decides, and approving writes
 * its next version.
 */
export function explainProfileChange(c: WireProfileChange, who: Who, reader: Reader): ItemContext {
  const country = countryName(c.marketCode);
  const proposer = who.actor(c.proposedBy);
  const decider = who.actor(c.decidedBy);
  const open = c.state === 'open';
  const evidence = c.evidence.slice(0, 4).map(([k, v]): [string, string | null] => [say(k) ?? 'Counted', say(v)]);
  const waited = daysSince(c.proposedAt, Date.now());
  return {
    subject: { kind: 'profile change', title: clip(c.change, 70) },
    summary: `${proposer ? `${proposer.name} proposed` : 'A change is proposed'}: ${clip(c.change, 160)} It is for the “${c.segment}” profile in ${country}.${c.effect && say(c.effect) ? ` ${say(c.effect)}` : ''}`,
    need: open ? (reader.decides ? 'Your decision: approve it, which writes the profile’s next version, or refuse it.' : 'The department’s lead to approve or refuse it.') : undefined,
    why: open ? 'A live profile is never edited in place, so every change waits here for a person.' : 'It was decided, and the decision stays on the profile’s record.',
    source: { label: c.evidence.length ? 'What converted, counted as the evidence on its page' : 'A proposal with no evidence on record', to: `/profiles/${c.profileId}`, when: shortDate(c.proposedAt) ?? undefined },
    broughtBy: proposer ? { ...proposer, when: shortDate(c.proposedAt) ?? undefined } : null,
    owner: who.lead(reader.department),
    involved: proposer && proposer.kind === 'agent' ? [proposer] : undefined,
    knows: who.knowers(reader.department),
    facts: [
      ...evidence,
      ['Points', c.fromPoints !== null || c.toPoints !== null ? `${c.fromPoints ?? '—'} to ${c.toPoints ?? '—'}` : null],
      ['Decided by', decider ? decider.name : null],
    ],
    next: open
      ? reader.decides
        ? {
            tip: `${waited >= 3 ? `It has waited ${waited} days. ` : ''}Read what was counted on its page, then approve to write the profile’s next version, or refuse to leave it as it is. Say why in your own words; it is kept with the decision.`,
            instead: 'If you are not sure, leave it: nothing changes on its own.',
          }
        : { tip: 'Nothing is needed from you. The department’s lead decides it.', instead: 'You can read what was counted on its page.' }
      : c.state === 'approved'
        ? { tip: 'Nothing to do: the profile’s next version carries it.' }
        : { tip: 'Nothing to do: it was refused, so the profile stays as it was.' },
    open: { to: `/profile-changes/${c.id}` },
    task: open && reader.decides ? { title: `Decide the profile change: ${clip(c.change, 60)}`, from: `/profile-changes/${c.id}` } : undefined,
  };
}

// --- Introductions ------------------------------------------------------------------------------

/**
 * An introduction asked of a colleague before any cold message (R1). The
 * colleague asked answers it; the cold sequence waits for them. Nothing here
 * writes to the prospect.
 */
export function explainIntro(
  i: WireIntro,
  who: Who,
  reader: Reader,
  extra: { answer?: ((made: boolean) => void) | undefined; busy?: boolean | undefined } = {},
): ItemContext {
  const prospect = i.prospect ?? 'a prospect whose name is not written down yet';
  const mine = i.viaHuman === reader.humanId;
  const colleague = byName(i.via, i.viaHuman, reader);
  const asked = shortDate(i.askedAt) ?? undefined;
  const outcome = say(i.outcome ?? null);
  const base = {
    subject: { kind: 'introduction', title: i.prospect ?? 'a prospect' },
    source: { label: `Asked from ${i.prospect ?? 'the prospect'}’s record`, to: `/prospects/${i.prospectId}`, when: asked },
    broughtBy: pressedBy(i.askedBy, i.askedByHuman, reader, 'asked for it', asked),
    owner: colleague,
    knows: who.knowers(reader.department),
    facts: [
      ['Asked of', mine ? 'You' : i.via],
      ['What was to be passed on', say(i.message ?? null)],
      ['Their answer', i.answeredAt ? `${i.state === 'made' ? 'Introduced' : 'Could not'} on ${dayWords(i.answeredAt)}` : null],
      ['Their words', outcome],
    ] as ItemContext['facts'],
    open: { to: `/introductions/${i.id}` },
  };
  if (i.state === 'asked') {
    const waited = daysSince(i.askedAt, Date.now());
    return mine
      ? {
          ...base,
          summary: `You were asked to introduce us to ${prospect}, because you know them. The cold sequence waits for your answer.`,
          need: 'Your answer: you introduced them, or you cannot.',
          why: `A colleague who knows a prospect is asked before any cold message, and that is you${waited > 0 ? `; it was asked ${sinceWords(i.askedAt, Date.now())}` : ''}.`,
          next: {
            tip: 'If you know them, introduce us yourself, in your own words, and then say so here: the cold sequence does not start. The platform writes to nobody for you.',
            instead: 'If you cannot, say that: the cold sequence starts in its next send window.',
            ...(extra.answer
              ? {
                  actions: [
                    { label: 'I introduced them', run: () => extra.answer?.(true), busy: extra.busy, primary: true },
                    { label: 'I can’t', run: () => extra.answer?.(false), busy: extra.busy },
                  ],
                }
              : {}),
          },
        }
      : {
          ...base,
          summary: `${i.via} was asked to introduce us to ${prospect}, before any cold message. The cold sequence waits for their answer.`,
          need: `${i.via}’s answer.`,
          why: 'An introduction goes before any cold step; it is in your view because it is your department’s prospect.',
          next: {
            tip: waited >= 7 ? `${i.via} has had it for ${waited} days. Ask them directly whether they can; only they can answer it.` : `Nothing is needed from you. Only ${i.via} can answer it, from Introductions or their day.`,
            instead: 'Nothing is sent to the prospect meanwhile.',
          },
        };
  }
  if (i.state === 'made') {
    return {
      ...base,
      summary: `${mine ? 'You' : i.via} introduced us to ${prospect}.${outcome ? ` In their words: “${outcome}”.` : ''}`,
      why: 'An introduction was made, so the cold sequence does not start and the thread is the colleague’s to continue.',
      next: { tip: 'Nothing to do for the prospect on our side: the colleague carries the conversation. If a deal comes of it, it appears on the prospect’s record.', instead: 'Ask them how it went if you want to know more.' },
    };
  }
  return {
    ...base,
    summary: `${mine ? 'You' : i.via} could not introduce us to ${prospect}.${outcome ? ` In their words: “${outcome}”.` : ''}`,
    why: 'The introduction was declined, so the prospect’s cold sequence starts in its next send window.',
    next: { tip: 'Nothing is needed now. Open the prospect to see its first email: it is a draft for a person, and nothing is sent without one.', instead: 'If you know someone else who knows them, ask them from the prospect’s record.', actions: [{ label: 'Open the prospect', to: `/prospects/${i.prospectId}` }] },
  };
}

// --- Samples ----------------------------------------------------------------------------------------

type SampleOutcome = 'sent' | 'ordered' | 'no_order';

/**
 * A sample asked of Production for a prospect (ADR-0136): asked, sent, then
 * ordered or not, each outcome a person records. What came of it counts
 * towards the profile that found them.
 */
export function explainSample(
  x: WireSample,
  who: Who,
  reader: Reader,
  extra: { record?: ((next: SampleOutcome) => void) | undefined; busy?: boolean | undefined } = {},
): ItemContext {
  const name = x.businessName ?? 'a prospect whose name is not written down yet';
  const product = `${productName(x.productCode)}${x.format ? `, ${x.format}` : ''}`;
  const production = who.lead('production');
  const waited = daysSince(x.requestedAt, Date.now());
  const record = (label: string, next: SampleOutcome, primary = false) => ({ label, run: () => extra.record?.(next), busy: extra.busy, ...(primary ? { primary } : {}) });
  const actions: NonNullable<ItemContext['next']['actions']> = !extra.record
    ? []
    : x.state === 'asked'
      ? [record('It was sent', 'sent'), record('They ordered', 'ordered', true), record('No order', 'no_order')]
      : x.state === 'sent'
        ? [record('They ordered', 'ordered', true), record('No order', 'no_order')]
        : [];
  return {
    subject: { kind: 'sample', title: `${product} for ${x.businessName ?? 'a prospect'}` },
    summary: `A sample of ${product.toLowerCase()} asked of Production for ${name}, ${sinceWords(x.requestedAt, Date.now())}.`,
    need:
      x.state === 'asked' ? 'Production to send it, then a call to the prospect and the outcome recorded.' : x.state === 'sent' ? 'The outcome: did they order?' : undefined,
    why: 'A sample is how a prospect that answered gets to try the product; what comes of it counts towards the profile that found them.',
    source: { label: 'A request to Production, made from the prospect’s record', to: `/requests/${x.requestId}`, when: shortDate(x.requestedAt) ?? undefined },
    broughtBy: pressedBy(x.requestedBy, x.requestedByHuman, reader, 'asked for it', shortDate(x.requestedAt)),
    owner: production ?? who.department('production'),
    knows: who.knowers('production'),
    facts: [
      ['Asked', dayWords(x.requestedAt)],
      ['Outcome recorded', x.outcomeAt ? dayWords(x.outcomeAt) : null],
    ],
    next:
      x.state === 'asked'
        ? {
            tip: `${waited >= 7 ? `It has been ${waited} days. ` : ''}Production has the request. Once it has gone, record “It was sent”; a week on, call them, and record whether they ordered.`,
            instead: waited >= 7 ? 'If it has stalled, open the request and ask Production why.' : 'Nothing is sent to the prospect by the platform; the call is yours.',
            ...(actions.length ? { actions } : {}),
          }
        : x.state === 'sent'
          ? { tip: 'It has been sent. Call them about a week on and record whether they ordered; it counts for or against the profile that found them.', instead: 'If they do not answer, record no order and mark them lost with a reason from their record.', ...(actions.length ? { actions } : {}) }
          : x.state === 'ordered'
            ? { tip: 'Nothing to do: they ordered, and it is counted towards the profile that found them.' }
            : { tip: 'Nothing to do: no order, which is counted too.', instead: 'If they are not coming back, mark them lost with a reason from their record.' },
    open: { to: `/prospects/${x.prospectId}`, label: 'Open the prospect' },
  };
}

// --- Our index ---------------------------------------------------------------------------------------------

/**
 * A row of our index (ADR-0129): a place or a person a plan found, asked
 * first so a tool is paid for only on a miss. A person stays sealed and is
 * never named; one nobody approves is shredded when its horizon passes.
 */
export function explainIndexEntry(e: WireIndexEntry, who: Who, reader: Reader, extra: { freshDays?: number; name?: string } = {}): ItemContext {
  const country = countryName(e.marketCode);
  const found = shortDate(e.firstSeen) ?? undefined;
  const agent = soleAgent(who, reader.department, 'found it under a plan');
  const title = extra.name ?? (e.kind === 'person' ? 'A person, sealed' : e.name ?? 'A place');
  const base = {
    subject: { kind: 'index entry', title },
    source: { label: `Found by ${toolName(e.sourceTool)}`, ...(found ? { when: found } : {}), ...(e.prospectId && e.kind === 'place' ? { to: `/prospects/${e.prospectId}` } : {}) },
    broughtBy: agent ? { ...agent, ...(found ? { when: found } : {}) } : null,
    owner: who.lead(reader.department),
    involved: agent ? [agent] : undefined,
    knows: who.knowers(reader.department),
    facts: [
      ['Where', e.kind === 'person' ? (e.at ?? country) : country],
      ['First seen', dayWords(e.firstSeen)],
      ['Refreshed', sinceWords(e.refreshedAt, Date.now())],
      ['Lookups saved', String(e.saved)],
      ['Readable until', e.readableUntil && !e.shredded ? dayWords(e.readableUntil) : null],
    ] as ItemContext['facts'],
    open: { to: `/our-index/${e.id}` },
  };
  if (e.kind === 'person') {
    return {
      ...base,
      summary: e.shredded ? 'A person a plan once found. Their key is gone and nothing of them is readable.' : `A person a plan found${e.at ? ` at ${e.at}` : ''}, sealed under their own key and never named here.`,
      need: e.shredded ? undefined : 'A decision only if they should be kept; otherwise nothing.',
      why: 'Our index is asked first by every plan so the same person is not paid for twice; their details stay sealed.',
      next: e.shredded
        ? { tip: 'Nothing to do: they are shredded and cannot be read.' }
        : {
            tip: e.readableUntil ? `Nothing is needed. Unless a person approves them as a buyer before ${dayWords(e.readableUntil)}, they are shredded then.` : 'Nothing is needed from you. Unless a person approves them as a buyer, they are shredded when their time is up.',
            instead: 'If they matter, approve them from Buyers before that day.',
          },
    };
  }
  const days = extra.freshDays;
  return {
    ...base,
    summary: `${e.name ?? 'A place whose name is not written down yet'} in ${country}, found by ${toolName(e.sourceTool)}. A plan asks our index first; ${plural(e.saved, 'lookup')} ${e.saved === 1 ? 'was' : 'were'} saved on it.`,
    need: 'Nothing from you.',
    why: e.name ? 'Every place a plan has found is kept so it is not paid for again.' : 'Google lets us keep only a place’s id, so it is named here once its own website has been read.',
    next: e.shredded
      ? { tip: 'Nothing to do.' }
      : e.fresh
        ? { tip: `Nothing is needed. It is fresh, so a plan that meets it again asks here and calls no tool${e.prospectId ? '; open its prospect to see where it stands' : ''}.`, ...(e.prospectId ? { actions: [{ label: 'Open the prospect', to: `/prospects/${e.prospectId}` }] } : {}) }
        : { tip: `Nothing is needed. It is older than ${days ?? 'the freshness'} days, so the next plan that needs it looks it up once and refreshes it.`, instead: e.prospectId ? 'Open its prospect if you want to see where it stands now.' : undefined },
  };
}

// --- A buyer ----------------------------------------------------------------------------------------------------

/**
 * A buyer at a company: a person’s details sealed under their key, or a
 * persona with no name. A buyer is approved only at an approved company, the
 * company first and under a token.
 */
export function explainBuyer(b: WireBuyer, who: Who, reader: Reader): ItemContext {
  const relationship = b.company?.relationship ?? 'unapproved';
  const companyApproved = relationship !== 'unapproved';
  const who_ = b.erased ? 'A buyer whose details were destroyed at their request' : b.persona ? `A ${b.roleTitle.toLowerCase()}, a role with no name on record` : `${b.name ?? 'A named buyer'}, ${b.roleTitle.toLowerCase()}`;
  const from = say(b.source);
  const reach = [b.linkedin && 'LinkedIn', b.phone && 'phone', b.email && 'email', b.switchboard && 'a switchboard'].filter(Boolean).join(', ');
  let tip: string;
  let instead: string | undefined;
  if (b.erased) tip = 'Nothing to do: their details were destroyed, and nothing can be written to them.';
  else if (b.approved) {
    tip = 'They are approved, so they may be written to: a person drafts or sends from their own mailbox. The platform sends nothing.';
    instead = b.prospectId ? 'Open the prospect to see the sequence and its next step.' : undefined;
  } else if (!companyApproved) {
    tip = reader.decides ? 'Approve the company first, from Buyers or Companies, under a token; only then can this buyer be approved.' : 'Nothing is needed from you. The lead or a superadmin approves the company first, then the buyer.';
    instead = 'Until then nobody writes to them.';
  } else {
    tip = reader.decides ? 'Its company is approved. Approve this buyer from Buyers if they should be written to.' : 'Nothing is needed from you. The lead or a superadmin approves the buyer.';
    instead = 'Leave them unapproved and nobody writes to them.';
  }
  return {
    // An erased buyer is never named again, here or anywhere.
    subject: { kind: 'buyer', title: b.persona || b.erased ? b.roleTitle : (b.name ?? b.roleTitle) },
    summary: `${who_} at ${b.companyName}${b.companyType ? ` (${b.companyType.toLowerCase()})` : ''}, in ${countryName(b.marketCode)}. ${b.approved ? 'Approved: they may be written to.' : 'Not approved yet.'}`,
    need: b.erased || b.approved ? undefined : companyApproved ? 'An approval of the buyer.' : 'An approved company first, then an approval of the buyer.',
    why: 'A buyer is the purchasing contact the validation asks for, one per company, so it is listed with how sure we are.',
    source: from ? { label: from, ...(b.prospectId ? { to: `/prospects/${b.prospectId}` } : {}) } : null,
    broughtBy: pressedBy(b.addedBy, b.addedByHuman, reader, 'added them'),
    owner: who.lead(reader.department),
    knows: who.knowers(reader.department),
    facts: [
      ['How sure', b.confidence],
      ['Reach', b.erased ? null : reach || 'No way to reach them on record'],
      ['Supplied today by', say(b.suppliedBy)],
      ['Its company', b.company ? `${b.company.name} — ${relationship === 'unapproved' ? 'not approved' : stateWords(relationship).toLowerCase()}` : `${b.companyName}, not in our records yet`],
    ],
    next: { tip, ...(instead ? { instead } : {}) },
    open: { to: `/buyers/${b.id}` },
  };
}

// --- A prospect -----------------------------------------------------------------------------------------------------

const WAY_IN: Record<NonNullable<WireProspect['wayIn']>, string> = {
  introduced: 'A colleague introduced us',
  asked: 'A colleague was asked for an introduction',
  warm: 'A colleague already knows them',
  cold: 'No one knows them: a cold first email',
};

/** What a stopped cadence leaves for a person, in its own words. */
const STOPPED: Record<string, { tip: string; instead?: string }> = {
  replied: { tip: 'They replied. Answer them yourself from the shared box, then file their objection from this page, or mark them lost with a reason.', instead: 'The platform never answers for you.' },
  out_of_office: { tip: 'An out-of-office came back. Nothing is needed until they are back; the next step waits.' },
  bounced: { tip: 'Their address bounced. Look for another way in, or mark them lost with a reason.' },
  lost: { tip: 'Nothing to do: they were marked lost.' },
  silence: { tip: 'Thirty days of silence after the last step ended the sequence. Decide: mark them lost with a reason, or try another way in yourself.', instead: 'Leaving it counts as nothing: it stays stopped.' },
  suppressed: { tip: 'They said stop, so nobody here writes to them again, in any plan. Nothing to do.' },
  erased: { tip: 'They asked to be forgotten. Nothing to do; a superadmin carries out the erasure.' },
  introduced: { tip: 'A colleague introduced them, so the cold sequence ended. The colleague carries the conversation.' },
  price_list_visit: { tip: 'They opened the price list. Follow up yourself while it is fresh.' },
};

/**
 * A prospect: a business a plan, a list or research found. Why this one and
 * why now come first, then the warm path before any cold step. Every step of
 * its sequence is a draft or a task for a person; the platform sends nothing
 * (ADR-0134, ADR-0136).
 */
export function explainProspect(p: WireProspect, r: WireProspectRecord | null, who: Who, reader: Reader): ItemContext {
  const name = p.businessName ?? p.domain ?? 'A business whose name is not written down yet';
  const country = countryName(p.marketCode);
  const lost = r?.lost ?? null;
  const supplier = p.foundAs === 'supplier';
  const competitor = p.foundAs === 'competitor';
  const agent = p.source === 'places' && r?.plan ? soleAgent(who, reader.department, 'ran the plan that found it') : null;
  const asked = (r?.intros ?? []).filter((i) => i.state === 'asked');
  // An introduction names who was asked by display name, as the prospect's page reads it.
  const askedOfMe = reader.displayName !== undefined && asked.some((i) => i.via === reader.displayName);
  const warm = r?.warmPath ?? [];
  const warmParties: Party[] = warm.map((w) => ({ name: w.who, kind: 'person', role: `${w.how === 'works_there' ? 'works there today' : 'worked there once'}, ${w.strength} link`, to: `/contacts/${w.contactId}` }));
  const score = r?.fitBreakdown.reduce((n, x) => n + x.points, 0) ?? p.fit?.score ?? null;
  const companyApproved = r?.company ? r.company.relationship !== 'unapproved' : false;
  const stopped = r?.cadence?.state === 'stopped' ? r.cadence.stopReason : null;

  const how = r?.plan ? `by the “${r.plan.segment}” plan in ${r.plan.territory}` : prospectSourceWords(p.source).toLowerCase();
  const summary = `${name}${p.domain && p.businessName ? ` (${p.domain})` : ''} was found ${how}, in ${country}${p.zone && p.zone !== r?.plan?.territory ? `, ${p.zone}` : ''}.${score !== null ? ` Its fit is ${score}${p.fit ? `, ${p.fit.tier}` : ''}.` : ''}${supplier ? ' Found as a supplier: to know of, never written to.' : competitor ? ' Found as a competitor: filed on the market, never written to.' : ''}`;

  let tip: string;
  let instead: string | undefined;
  let actions: ItemContext['next']['actions'];
  if (r?.erasureOpen) {
    tip = 'They asked to be forgotten, so nothing is sent and every sequence ended. A superadmin carries out the erasure; nothing is needed from you.';
  } else if (lost) {
    const reason = (LOST_WORDS as Record<string, string>)[lost.reason];
    tip = `Nothing to do: it was marked lost${reason ? ` (${reason.toLowerCase()})` : ''} and counts against its plan.`;
    instead = 'If they come back on their own, answer them from the shared box.';
  } else if (p.stage === 'excluded') {
    const why = p.exclusion ? EXCLUSION_WORDS[p.exclusion] : 'It was taken off the list';
    tip = `Taken off the list: ${why.charAt(0).toLowerCase()}${why.slice(1)}. Nothing is needed; nothing is written to it.`;
  } else if (supplier || competitor) {
    tip = `Read what is known of it for the market; nobody ${supplier ? 'at a supplier' : 'at a competitor'} is written to.`;
    if (reader.decides) {
      instead = 'To find more like it, start from here.';
      actions = [{ label: supplier ? 'Find suppliers like this' : 'Find more competitors like this', to: `/prospecting/setup?market=${p.marketCode}&likeProspect=${p.id}` }];
    }
  } else if (stopped && STOPPED[stopped]) {
    ({ tip, instead } = STOPPED[stopped]!);
  } else if (askedOfMe) {
    tip = 'You were asked to introduce us to them. Say whether you did or cannot from this page; the cold sequence waits for you.';
  } else if (asked.length > 0) {
    tip = `Waiting on ${asked[0]!.via} to answer the introduction request; the cold sequence waits with it. Nothing is needed from you.`;
    instead = 'Ask them directly if it drags.';
  } else if (warm.length > 0 && (p.stage === 'read' || p.stage === 'enriched' || p.stage === 'drafted') && (r?.intros.length ?? 0) === 0) {
    tip = `${warm[0]!.who}${warm.length > 1 ? ` and ${plural(warm.length - 1, 'other colleague')}` : ''} already know${warm.length === 1 ? 's' : ''} them. Ask one to introduce us from this page before any cold email.`;
    instead = p.stage === 'drafted' ? 'Or go ahead with the drafted first email, read and sent by a person.' : 'Or let the cold sequence start; a warm path is quicker.';
  } else if (p.stage === 'drafted') {
    tip = 'A first email is waiting as a draft in the shared box. Read it, edit it if it needs it, and send it yourself or approve it: a person always does, the platform sends nothing.';
    instead = 'If it is not worth writing to, mark it lost with a reason, or take it off the list.';
  } else if (p.stage === 'enriched') {
    tip = 'It has a checked address, so the first email is drafted next, as a draft for a person. Nothing is needed yet.';
    instead = r && !companyApproved ? 'Nothing writes to a company until a person approves it: approve it from this page under a token.' : undefined;
  } else if (p.stage === 'read') {
    tip = 'Its website has been read and scored. If it clears the bar its address is looked for next; nothing is needed yet.';
  } else {
    tip = 'It was just found; its website is read next. Nothing is needed from you.';
  }

  return {
    subject: { kind: 'prospect', title: name },
    summary,
    need: r?.erasureOpen || lost || p.stage === 'excluded' || supplier || competitor ? undefined : r && !companyApproved && p.stage !== 'discovered' ? 'Its company approved before anything is written to it, then a person to act on the first draft.' : p.stage === 'drafted' ? 'A person to read the first draft and decide whether it goes.' : undefined,
    why: r?.whyNow ?? (score !== null ? `It scored ${score} against its profile, so it is in front of you.` : 'A plan, a list or research found it for this department.'),
    source: r?.plan ? { label: `The “${r.plan.segment}” plan in ${r.plan.territory}`, to: `/prospecting/plans/${r.plan.id}`, when: shortDate(p.createdAt) ?? undefined } : { label: `${prospectSourceWords(p.source)}; no plan claims it`, when: shortDate(p.createdAt) ?? undefined },
    broughtBy: agent ? { ...agent, when: shortDate(p.createdAt) ?? undefined } : null,
    owner: who.lead(reader.department),
    involved: [...(agent ? [agent] : withRole(who.agentsOf(reader.department), 'reads, scores and drafts for it')), ...asked.map((i): Party => ({ name: i.via, kind: 'person', role: 'asked to introduce us' }))],
    knows: [...warmParties, ...who.knowers(reader.department)],
    facts: [
      ['Way in', p.wayIn ? WAY_IN[p.wayIn] : null],
      ['Stage', STAGE_WORDS[p.stage]],
      ['Buys from today', r?.incumbent ? r.incumbent : null],
      ['Address', p.hasAddress ? `${p.addressKind === 'role' ? 'A shared' : 'A person’s'} address on file${p.verification ? `, ${p.verification}` : ''}` : 'None found yet'],
      ['Signals', r && r.signals.length > 0 ? String(r.signals.length) : null],
      ['Samples', r && r.samples.length > 0 ? String(r.samples.length) : null],
      ['Also met by', r?.alsoMet?.length ? plural(r.alsoMet.length, 'other plan') : null],
    ],
    next: { tip, ...(instead ? { instead } : {}), ...(actions ? { actions } : {}) },
    open: { to: `/prospects/${p.id}` },
  };
}

// --- A report ---------------------------------------------------------------------------------------------------------

/**
 * A market report or the portfolio, as the document it will be: composed on
 * the server from the records when it is read, so what is read, copied and
 * filed to Drive are the same words.
 */
export function explainReport(args: { title: string; portfolio: boolean; queued: boolean }, who: Who, reader: Reader): ItemContext {
  return {
    subject: { kind: 'report', title: args.title },
    summary: `${args.title} is composed now, from the ${args.portfolio ? 'portfolio of markets' : 'market’s'} records; it is not a separate file until someone files it to Drive.`,
    need: reader.decides ? 'Your choice to file it to Drive, if others should have it.' : undefined,
    why: 'A report is what a market’s research, prices and decisions say, written for reading outside the platform.',
    source: { label: args.portfolio ? 'The records of every researched market' : 'The market’s own records: its screen, findings, prices and decision' },
    broughtBy: null,
    owner: who.lead(reader.department),
    involved: who.agentsOf(reader.department),
    knows: who.knowers(reader.department),
    next: reader.decides
      ? args.queued
        ? { tip: 'It is queued: the ledger mirror files it to Drive on its next hourly run. Nothing more to do.' }
        : { tip: 'Read it through, then save it to Drive if it is to be shared; the copy that leaves names nobody. To change it, change the records: ask Otis in the chat.', instead: 'Or copy it as it is and use it elsewhere.' }
      : { tip: 'Nothing is needed from you. Read it, or copy it; the lead files it to Drive.', instead: 'To change what it says, ask the lead or Otis to change the records.' },
  };
}
